import React, { useCallback, useEffect, useState } from "react"
import { ToastContainer, toast } from "react-toastify"
import { request } from "../../services/request"
import { SchoolDomainData, SchoolDomainResponse } from "../../services/authinterface"
import Table, { TableAction } from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import { useNavigate } from "react-router-dom"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { donwloadCSV, downloadPDF } from "../../utils/exportFunctions"
import { DeleteIcon, EditIcon } from "../../icons/webSvg"
import ConfirmModal from "../../components/common/ConfirmModal"

const SchoolDomain: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [schoolDomain, setSchoolDomain] = useState<SchoolDomainData[]>([])
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalItems, setTotalItems] = useState(100)
  const [search, setSearch] = useState("")
  const debounceText = useDebounce(search, 500)
  const [sort, setSort] = useState("DESC")
  const [selectedRows, setSelectedRows] = React.useState<any>([])
  const [sortCol, setSortCol] = useState("createdAt")
  const [deleteRow, setDeleteRow] = useState<SchoolDomainData | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const fetchSchoolDomain = useCallback(async () => {
    try {
      dispatch(changeloaderState(true))
      const params = {
        page: page,
        limit: rowsPerPage,
        search: debounceText,
        sort: sort,
        sortCol: sortCol,
      }
      const response = await request<SchoolDomainResponse>({
        url: "/adminCommon/listSchoolDomains",
        method: "POST",
        data: params,
      })

      if (response.data) {
        setSchoolDomain(response.data)
        setTotalItems(response.total)
      } else {
        toast.error("Failed to fetch: " + response.message)
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    } finally {
      dispatch(changeloaderState(false))
    }
  }, [dispatch, page, rowsPerPage, debounceText, sort, sortCol])

  useEffect(() => {
    fetchSchoolDomain()
  }, [page, rowsPerPage, debounceText, sort, fetchSchoolDomain])

  useEffect(() => {
    if (debounceText) {
      setPage(1)
    }
  }, [debounceText])

  const addAction = {
    label: "Create",
    type: "button",
    onClick: (row: SchoolDomainData) => {
      navigate(`/school-domain/add`)
    },
    className: "resetBtn",
  }

  const handleSchoolDomainStatusUpdate = async (row: SchoolDomainData) => {
    const payload = {
      id: row?.id,
      active: row?.active ? false : true,
    }
    try {
      const response = await request({
        url: `/adminCommon/changeStatusSchoolDomain`,
        method: "POST",
        data: payload,
      })
      if (response.data) {
        toast.success(response.message)
        fetchSchoolDomain()
      } else {
        toast.error(response.message)
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    }
  }

  const handleSchoolDomainDelete = async () => {
    if (!deleteRow) return
    setIsDeleting(true)
    try {
      const response = await request({
        url: `/adminCommon/deleteSchoolDomain`,
        method: "POST",
        data: { id: deleteRow.id },
      })
      if (response.data) {
        toast.success(response.message)
        setDeleteRow(null)
        // deleted the only row on this page, so go back one page
        if (schoolDomain.length === 1 && page > 1) {
          setPage(page - 1)
        } else {
          fetchSchoolDomain()
        }
      } else {
        toast.error(response.message)
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const columns = [
    {
      name: "Domain",
      value: (row: SchoolDomainData) => row.domain,
      id: "domain",
      className: "long-text-devices"
    },
    {
      name: "Institution Name",
      value: (row: SchoolDomainData) => row.institution_name,
      id: "institution_name",
      className: "long-text-devices"
    },
    {
      name: "City",
      value: (row: SchoolDomainData) => row.city,
      id: "city",
      className: "long-text-devices"
    },
    {
      name: "State",
      value: (row: SchoolDomainData) => row.state,
      id: "state",
      className: "long-text-devices"
    },
    {
      name: "Status",
      value: (row: SchoolDomainData) => (
        <PrimaryButton
          label={row?.active === true ? "Active" : "Inactive"}
          className='subscription-status-btn m-0'
          onClick={() => handleSchoolDomainStatusUpdate(row)}
        />
      ),
      id: "status",
    },
  ]

  const action: TableAction[] = [
    {
      label: "",
      isRedirect: true,
      link: (row: SchoolDomainData) => `/school-domain/edit/${row?.id}`,
      className: "table-btn-2",
      icon: (
        <EditIcon />
      ),
    },
    {
      label: "",
      onClick: (row: SchoolDomainData) => setDeleteRow(row),
      className: "table-btn-2 table-btn-delete",
      icon: (
        <DeleteIcon />
      ),
    },
  ]

  const fetchAllSchoolDomainData = async () => {
    try {
      const params = {
        page: 1,
        search: debounceText,
        limit: 100000,
        sort: sort,
        sortCol: sortCol,
      }

      const response = await request<SchoolDomainResponse>({
        url: "/adminCommon/listSchoolDomains",
        method: "POST",
        data: params,
      })

      if (response.data) {
        return response.data
      } else {
        toast.error("Failed to fetch all data: " + response.message)
        return []
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
      return []
    }
  }

  const exportCSV = async () => {
    const allData = await fetchAllSchoolDomainData()
    if (!allData.length) {
      toast.error("No data available to export")
      return
    }
    const headers = [
      "Sr. No.",
      "Domain",
      "Institution Name",
      "City",
      "State",
      "Status"
    ]

    const rows = allData.map((s, index) => [
      index + 1,
      s.domain || "-",
      s.institution_name || "-",
      s.city || "-",
      s.state || "-",
      s.active ? "Active" : "Inactive",
    ])

    donwloadCSV(headers, rows, "SchoolDomain.csv")
  }

  const exportPDF = async () => {

    const allData = await fetchAllSchoolDomainData()

    if (!allData.length) {
      toast.error("No data available to export")
      return
    }

    const headers = [
      "Sr. No.",
      "Domain",
      "Institution Name",
      "City",
      "State",
      "Status"
    ]

    const rows = allData.map((s, index) => [
      index + 1,
      s.domain || "-",
      s.institution_name || "-",
      s.city || "-",
      s.state || "-",
      s.active ? "Active" : "Inactive",
    ])

    downloadPDF(headers, rows, "SchoolDomain")
  }

  return (
    <section className=' dashboard-section dashboard-item '>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />
      <ConfirmModal
        show={!!deleteRow}
        title='Delete School Domain'
        message={<>Are you sure you want to delete <strong>{deleteRow?.domain}</strong>?</>}
        confirmLabel='Delete'
        loadingLabel='Deleting...'
        isLoading={isDeleting}
        danger
        onConfirm={handleSchoolDomainDelete}
        onClose={() => setDeleteRow(null)}
      />

      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div className='dashboard-table-title-section'>
              <h2>School</h2>
              <div className='dashboard-table-title-right-side d-flex gap-2'>
                <PrimaryButton
                  label='Export CSV'
                  className='max-w-max-content'
                  onClick={exportCSV}
                />
                <PrimaryButton
                  label='Export PDF'
                  className='max-w-max-content'
                  onClick={exportPDF}
                />
              </div>
            </div>
          </div>
          <div className='col-12 mt-4'>
            <div className='table-container-main'>
              <div className='table-details-section'></div>
              <Table
                rowData={schoolDomain}
                columns={columns}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalItems={totalItems}
                setSortColumn={setSortCol}
                setSortOrder={setSort}
                sortColumn={sortCol}
                sortOrder={sort}
                isSearch
                search={search}
                setSearch={setSearch}
                action={action}
                {...(selectedRows.length > 0 && { addAction })}
                setSelected={setSelectedRows}
                addAction={addAction}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default SchoolDomain
